import { Alert, AlertDescription, AlertTitle, Button } from '@devizy/ui';
import { Link, createFileRoute } from '@tanstack/react-router';
import * as React from 'react';
import { useTranslations } from 'use-intl';

import { PageHeader } from '@/components/dashboard/page-header';
import { FeedFiltersClient } from '@/components/feed/feed-filters';
import { FeedList } from '@/components/feed/feed-list';
import {
  parseFeedFiltersFromSearchParams,
  parseFeedSortFromSearchParams,
  type SearchParamsLike,
} from '@/components/feed/parse-filters';
import { PrivateInvitations } from '@/components/feed/private-invitations';
import { trpc } from '@/lib/trpc/client';

export const Route = createFileRoute('/_app/dashboard/bidder/feed')({
  validateSearch: (search: Record<string, unknown>): SearchParamsLike => {
    const result: SearchParamsLike = {};
    for (const [key, value] of Object.entries(search)) {
      if (typeof value === 'string') {
        result[key] = value;
      } else if (Array.isArray(value)) {
        result[key] = value.map((v) => String(v));
      }
    }
    return result;
  },
  component: BidderFeedPage,
});

function BidderFeedPage(): React.ReactElement {
  const t = useTranslations('feed');
  const tErrors = useTranslations('feed.errors');
  const search = Route.useSearch();
  const { data: me, isLoading } = trpc.user.me.useQuery();

  if (isLoading) {
    return (
      <div className="mt-6 space-y-4">
        <PageHeader title={t('title')} subtitle={t('subtitle')} />
      </div>
    );
  }

  const status = me?.bidderVerificationStatus ?? null;
  const canAccess = status === 'VERIFIED' || status === 'PENDING_REVIEW';
  const isRejected = status === 'REJECTED';
  const profileLink =
    me?.bidderType === 'COMPANY' ? '/profile/bidder/company' : '/profile/bidder/individual';
  const initialFilters = parseFeedFiltersFromSearchParams(search);
  const initialSort = parseFeedSortFromSearchParams(search);

  return (
    <div className="space-y-4">
      <PageHeader title={t('title')} subtitle={t('subtitle')} />
      <PrivateInvitations verified={canAccess} />
      {canAccess ? (
        <div className="flex flex-col gap-4 lg:flex-row">
          <FeedFiltersClient
            initial={initialFilters}
            initialSort={initialSort}
            pathname="/dashboard/bidder/feed"
          />
          <div className="min-w-0 flex-1">
            <FeedList filters={initialFilters} sortBy={initialSort} />
          </div>
        </div>
      ) : (
        <Alert variant="error">
          <AlertTitle>{t('title')}</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>{isRejected ? tErrors('profileRejected') : tErrors('needsCompleteProfile')}</p>
            {!isRejected ? (
              <Button asChild size="sm">
                <Link to={profileLink}>{tErrors('completeProfileCta')}</Link>
              </Button>
            ) : null}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
